<template>
  <div class="w-100">
    <div class="data" v-if="loaded">

      <div v-if="successMessage" class="alert alert-success mt-3" role="alert">
        Changes have been submitted successfully!
      </div>

      <ul class="mb-3" v-if="errors.length">
        <li v-for="error in errors" :key="error" class="text-danger">{{ error }}</li>
      </ul>

      <form class="mt-4 mb-5" @submit.prevent="updateCompany">

        <!-- ══════════════════════════════
             ABOUT US
        ══════════════════════════════ -->
        <div class="form-row">
          <div class="form-group col-lg-6">
            <label><strong>About Us</strong></label>
            <textarea v-model="updateForm.about_us" class="form-control" placeholder="About Us" />
          </div>
        </div>

        <label><strong>About Us Translations</strong></label>
        <div class="form-row">
          <div
              v-for="(_, langCode) in updateForm.translations.about_us"
              :key="langCode"
              class="input-group col-lg-6 my-2"
          >
            <div class="input-group-prepend">
              <span class="input-group-text">{{ langCode }}</span>
            </div>
            <textarea
                v-model="updateForm.translations.about_us[langCode]"
                class="form-control"
                placeholder="Translation"
            />
            <span class="input-group-append">
              <button
                  type="button"
                  class="btn btn-outline-danger"
                  @click="removeTranslation('about_us', langCode)"
              >✕</button>
            </span>
          </div>
        </div>
        <div class="form-row mt-2" v-if="availableLanguages('about_us').length">
          <select class="mx-2 form-control col-lg-3" v-model="selectedLanguages.about_us">
            <option value="">Select language...</option>
            <option
                v-for="lang in availableLanguages('about_us')"
                :key="lang.code"
                :value="lang.code"
            >{{ lang.name }}</option>
          </select>
          <button
              type="button"
              class="btn btn-primary mx-1"
              :disabled="!selectedLanguages.about_us"
              @click="addTranslation('about_us')"
          ><i class="fas fa-plus"></i> Add</button>
        </div>
        <hr>

        <!-- ══════════════════════════════
             COMPANY HIGHLIGHTS
        ══════════════════════════════ -->
        <div class="form-row my-2">
          <div class="form-group col-lg-6">
            <label><strong>Company Highlights</strong></label>
            <vue-editor v-model="updateForm.company_highlights" />
          </div>
        </div>

        <label><strong>Company Highlights Translations</strong></label>
        <div class="form-row">
          <div
              v-for="(_, langCode) in updateForm.translations.company_highlights"
              :key="langCode"
              class="col-lg-6 my-2"
          >
            <div class="d-flex align-items-center mb-1">
              <span class="badge badge-secondary mr-2">{{ langCode }}</span>
              <button
                  type="button"
                  class="btn btn-sm btn-outline-danger ml-auto"
                  @click="removeTranslation('company_highlights', langCode)"
              >✕</button>
            </div>
            <vue-editor v-model="updateForm.translations.company_highlights[langCode]" />
          </div>
        </div>
        <div class="form-row mt-2" v-if="availableLanguages('company_highlights').length">
          <select class="mx-2 form-control col-lg-3" v-model="selectedLanguages.company_highlights">
            <option value="">Select language...</option>
            <option
                v-for="lang in availableLanguages('company_highlights')"
                :key="lang.code"
                :value="lang.code"
            >{{ lang.name }}</option>
          </select>
          <button
              type="button"
              class="btn btn-primary mx-1"
              :disabled="!selectedLanguages.company_highlights"
              @click="addTranslation('company_highlights')"
          ><i class="fas fa-plus"></i> Add</button>
        </div>
        <hr>

        <!-- ══════════════════════════════
             SHORT DESCRIPTION
        ══════════════════════════════ -->
        <div class="form-row">
          <div class="form-group col-lg-6">
            <label><strong>Short Description</strong></label>
            <textarea v-model="updateForm.short_description" class="form-control" placeholder="Short description" />
          </div>
        </div>

        <label><strong>Short Description Translations</strong></label>
        <div class="form-row">
          <div
              v-for="(_, langCode) in updateForm.translations.short_description"
              :key="langCode"
              class="input-group col-lg-6 my-2"
          >
            <div class="input-group-prepend">
              <span class="input-group-text">{{ langCode }}</span>
            </div>
            <textarea
                v-model="updateForm.translations.short_description[langCode]"
                class="form-control"
                placeholder="Translation"
            />
            <span class="input-group-append">
              <button type="button" class="btn btn-outline-danger" @click="removeTranslation('short_description', langCode)">✕</button>
            </span>
          </div>
        </div>
        <div class="form-row mt-2" v-if="availableLanguages('short_description').length">
          <select class="mx-2 form-control col-lg-3" v-model="selectedLanguages.short_description">
            <option value="">Select language...</option>
            <option v-for="lang in availableLanguages('short_description')" :key="lang.code" :value="lang.code">{{ lang.name }}</option>
          </select>
          <button type="button" class="btn btn-primary mx-1" :disabled="!selectedLanguages.short_description" @click="addTranslation('short_description')">
            <i class="fas fa-plus"></i> Add
          </button>
        </div>
        <hr>

        <!-- ══════════════════════════════
             TERMS AND CONDITIONS
        ══════════════════════════════ -->
        <div class="form-row">
          <div class="form-group col-lg-6">
            <label><strong>Terms And Conditions</strong></label>
            <vue-editor v-model="updateForm.terms_and_conditions" />
          </div>
        </div>

        <label><strong>Terms And Conditions Translations</strong></label>
        <div class="form-row">
          <div v-for="(_, langCode) in updateForm.translations.terms_and_conditions" :key="langCode" class="col-lg-6 my-2">
            <div class="d-flex align-items-center mb-1">
              <span class="badge badge-secondary mr-2">{{ langCode }}</span>
              <button type="button" class="btn btn-sm btn-outline-danger ml-auto" @click="removeTranslation('terms_and_conditions', langCode)">✕</button>
            </div>
            <vue-editor v-model="updateForm.translations.terms_and_conditions[langCode]" />
          </div>
        </div>
        <div class="form-row mt-2" v-if="availableLanguages('terms_and_conditions').length">
          <select class="mx-2 form-control col-lg-3" v-model="selectedLanguages.terms_and_conditions">
            <option value="">Select language...</option>
            <option v-for="lang in availableLanguages('terms_and_conditions')" :key="lang.code" :value="lang.code">{{ lang.name }}</option>
          </select>
          <button type="button" class="btn btn-primary mx-1" :disabled="!selectedLanguages.terms_and_conditions" @click="addTranslation('terms_and_conditions')">
            <i class="fas fa-plus"></i> Add
          </button>
        </div>
        <hr>

        <!-- ══════════════════════════════
             REFUND POLICY
        ══════════════════════════════ -->
        <div class="form-row">
          <div class="form-group col-lg-6">
            <label><strong>Refund Policy</strong></label>
            <vue-editor v-model="updateForm.refund_policy" />
          </div>
        </div>

        <label><strong>Refund Policy Translations</strong></label>
        <div class="form-row">
          <div v-for="(_, langCode) in updateForm.translations.refund_policy" :key="langCode" class="col-lg-6 my-2">
            <div class="d-flex align-items-center mb-1">
              <span class="badge badge-secondary mr-2">{{ langCode }}</span>
              <button type="button" class="btn btn-sm btn-outline-danger ml-auto" @click="removeTranslation('refund_policy', langCode)">✕</button>
            </div>
            <vue-editor v-model="updateForm.translations.refund_policy[langCode]" />
          </div>
        </div>
        <div class="form-row mt-2" v-if="availableLanguages('refund_policy').length">
          <select class="mx-2 form-control col-lg-3" v-model="selectedLanguages.refund_policy">
            <option value="">Select language...</option>
            <option v-for="lang in availableLanguages('refund_policy')" :key="lang.code" :value="lang.code">{{ lang.name }}</option>
          </select>
          <button type="button" class="btn btn-primary mx-1" :disabled="!selectedLanguages.refund_policy" @click="addTranslation('refund_policy')">
            <i class="fas fa-plus"></i> Add
          </button>
        </div>
        <hr>

        <!-- ══════════════════════════════
             SHIPPING POLICY
        ══════════════════════════════ -->
        <div class="form-row">
          <div class="form-group col-lg-6">
            <label><strong>Shipping Policy</strong></label>
            <vue-editor v-model="updateForm.shipping_policy" />
          </div>
        </div>

        <label><strong>Shipping Policy Translations</strong></label>
        <div class="form-row">
          <div v-for="(_, langCode) in updateForm.translations.shipping_policy" :key="langCode" class="col-lg-6 my-2">
            <div class="d-flex align-items-center mb-1">
              <span class="badge badge-secondary mr-2">{{ langCode }}</span>
              <button type="button" class="btn btn-sm btn-outline-danger ml-auto" @click="removeTranslation('shipping_policy', langCode)">✕</button>
            </div>
            <vue-editor v-model="updateForm.translations.shipping_policy[langCode]" />
          </div>
        </div>
        <div class="form-row mt-2" v-if="availableLanguages('shipping_policy').length">
          <select class="mx-2 form-control col-lg-3" v-model="selectedLanguages.shipping_policy">
            <option value="">Select language...</option>
            <option v-for="lang in availableLanguages('shipping_policy')" :key="lang.code" :value="lang.code">{{ lang.name }}</option>
          </select>
          <button type="button" class="btn btn-primary mx-1" :disabled="!selectedLanguages.shipping_policy" @click="addTranslation('shipping_policy')">
            <i class="fas fa-plus"></i> Add
          </button>
        </div>
        <hr>

        <!-- ══════════════════════════════
             PRIVACY POLICY
        ══════════════════════════════ -->
        <div class="form-row my-2">
          <div class="form-group col-lg-6">
            <label><strong>Privacy Policy</strong></label>
            <vue-editor v-model="updateForm.privacy_policy" />
          </div>
        </div>

        <label><strong>Privacy Policy Translations</strong></label>
        <div class="form-row">
          <div v-for="(_, langCode) in updateForm.translations.privacy_policy" :key="langCode" class="col-lg-6 my-2">
            <div class="d-flex align-items-center mb-1">
              <span class="badge badge-secondary mr-2">{{ langCode }}</span>
              <button type="button" class="btn btn-sm btn-outline-danger ml-auto" @click="removeTranslation('privacy_policy', langCode)">✕</button>
            </div>
            <vue-editor v-model="updateForm.translations.privacy_policy[langCode]" />
          </div>
        </div>
        <div class="form-row mt-2" v-if="availableLanguages('privacy_policy').length">
          <select class="mx-2 form-control col-lg-3" v-model="selectedLanguages.privacy_policy">
            <option value="">Select language...</option>
            <option v-for="lang in availableLanguages('privacy_policy')" :key="lang.code" :value="lang.code">{{ lang.name }}</option>
          </select>
          <button type="button" class="btn btn-primary mx-1" :disabled="!selectedLanguages.privacy_policy" @click="addTranslation('privacy_policy')">
            <i class="fas fa-plus"></i> Add
          </button>
        </div>
        <hr>

        <!-- ══════════════════════════════
             CONTACT INFO
        ══════════════════════════════ -->
        <div class="form-row mt-2">
          <div class="form-group col-lg-2">
            <label><strong>Fax</strong></label>
            <input v-model="updateForm.fax" class="form-control" placeholder="Fax" />
          </div>
          <div class="form-group col-lg-2">
            <label><strong>Whatsapp Number</strong></label>
            <input v-model="updateForm.whatsapp_number" class="form-control" placeholder="Whatsapp number" />
          </div>
          <div class="form-group col-lg-4">
            <label><strong>Telephones</strong></label>
            <div
                v-for="(_, index) in updateForm.telephones"
                :key="`phone-${index}`"
                class="d-flex align-items-center mb-2"
            >
              <input
                  type="text"
                  v-model="updateForm.telephones[index]"
                  class="form-control"
                  placeholder="Enter Phone Number"
              />
              <button type="button" class="btn btn-sm btn-outline-success ml-2" @click="addPhoneField(index + 1)">+</button>
              <button
                  type="button"
                  class="btn btn-sm btn-outline-danger ml-1"
                  v-show="updateForm.telephones.length > 1"
                  @click="removePhoneField(index)"
              >✕</button>
            </div>
            <button
                v-if="!updateForm.telephones.length"
                type="button"
                class="btn btn-outline-primary btn-sm"
                @click="addPhoneField(0)"
            ><i class="fas fa-plus"></i> Add Phone</button>
          </div>
        </div>

        <div class="form-row mt-2">
          <div class="form-group col-lg-4">
            <label><strong>Email</strong></label>
            <input v-model="updateForm.email" class="form-control" placeholder="Email" />
          </div>
        </div>
        <hr>

        <!-- ══════════════════════════════
             ADDRESS
        ══════════════════════════════ -->
        <label><strong>Address</strong></label>
        <div class="form-row mt-2">
          <div class="form-group col-lg-4">
            <label>Address Description</label>
            <input v-model="updateForm.address.address_description" class="form-control" placeholder="Address description" />
          </div>
        </div>
        <div class="form-row mt-2">
          <div class="form-group col-lg-2">
            <label>Country</label>
            <country-search-box @selected="(id) => id" />
          </div>
          <div class="form-group col-lg-2">
            <label>City</label>
            <city-search-box @selected="(id) => updateForm.address.city_id = id" />
          </div>
          <div class="form-group col-lg-2">
            <label>Latitude</label>
            <input v-model="updateForm.address.latitude" class="form-control" placeholder="Latitude" />
          </div>
          <div class="form-group col-lg-2">
            <label>Longitude</label>
            <input v-model="updateForm.address.longitude" class="form-control" placeholder="Longitude" />
          </div>
        </div>
        <hr>

        <!-- ══════════════════════════════
             LOGO & IMAGES
        ══════════════════════════════ -->
        <div class="form-group col-lg-4">
          <label><strong>Logo</strong></label>
          <file-upload
              :multiple="false"
              label="Select an image..."
              @done="file_name => updateForm.logo = file_name"
              class="mt-3"
          />
        </div>
        <div class="form-group col-lg-4">
          <label><strong>Images</strong></label>
          <MultipleFilesUpload
              label="Select multiple images..."
              @done="file_name => updateForm.images = file_name"
              class="mt-3"
              :photos="photos"
          />
        </div>
        <hr>

        <!-- ══════════════════════════════
             SOCIAL MEDIAS
        ══════════════════════════════ -->
        <div class="form-group col-lg-4">
          <label><strong>Social Medias</strong></label>
          <button
              type="button"
              class="btn btn-sm btn-outline-success ml-2"
              @click="addSocialMedia"
          ><i class="fas fa-plus"></i> Add</button>

          <div
              v-for="(item, index) in updateForm.social_medias"
              :key="`social-${index}`"
              class="d-flex align-items-center mt-2"
          >
            <select v-model="updateForm.social_medias[index].platform" class="form-control mr-2">
              <option value="">Select platform...</option>
              <option v-for="platform in platforms" :key="platform" :value="platform">{{ platform }}</option>
            </select>
            <input
                type="text"
                v-model="updateForm.social_medias[index].url"
                placeholder="URL"
                class="form-control mr-2"
            />
            <button
                type="button"
                class="btn btn-sm btn-outline-danger"
                @click="removeSocialMedia(index)"
            >✕</button>
          </div>
        </div>
        <hr>

        <!-- ══════════════════════════════
             ACHIEVEMENTS
        ══════════════════════════════ -->
        <div class="form-group col-lg-6">
          <label><strong>Achievements</strong></label>
          <button
              type="button"
              class="btn btn-sm btn-outline-success ml-2"
              @click="addAchievement"
          ><i class="fas fa-plus"></i> Add</button>

          <div
              v-for="(item, index) in updateForm.achievements"
              :key="`achievement-${index}`"
              class="border rounded p-3 mt-2"
          >
            <div class="d-flex justify-content-between align-items-center mb-2">
              <strong>Achievement #{{ index + 1 }}</strong>
              <button type="button" class="btn btn-sm btn-outline-danger" @click="removeAchievement(index)">✕ Remove</button>
            </div>
            <div class="form-group">
              <label>Title</label>
              <input type="text" v-model="updateForm.achievements[index].title" class="form-control" placeholder="Title" />
            </div>
            <div class="form-group">
              <label>Photo</label>
              <file-upload
                  :multiple="false"
                  label="Select an image..."
                  @done="file_name => updateForm.achievements[index].photo = file_name"
              />
            </div>
          </div>
        </div>
        <hr>

        <button type="submit" class="btn btn-primary mt-1 mr-2">Update</button>

      </form>
    </div>

    <div class="mt-5 pt-5">
      <loading v-if="!loaded" />
    </div>
  </div>
</template>

<script>
import { config } from "../helpers";
import CountrySearchBox from "../components/CountrySearchBox.vue";
import CitySearchBox from "../components/CitySearchBox.vue";
import FileUpload from "@/components/FileUpload";
import MultipleFilesUpload from "@/components/MultipleFilesUpload";
import { VueEditor } from "vue2-editor";

// کلید‌های قابل ترجمه
const TRANSLATABLE_KEYS = [
  "about_us",
  "company_highlights",
  "short_description",
  "terms_and_conditions",
  "refund_policy",
  "shipping_policy",
  "privacy_policy",
];

const ALL_LANGUAGES = [
  { code: "ar", name: "Arabic" },
  { code: "fr", name: "French" },
  { code: "es", name: "Spanish" },
];

export default {
  name: "CompanyInfoEdit",

  components: {
    MultipleFilesUpload,
    FileUpload,
    "country-search-box": CountrySearchBox,
    "city-search-box": CitySearchBox,
    VueEditor,
  },

  data() {
    // ساخت selectedLanguages جداگانه برای هر سکشن
    const selectedLanguages = {};
    TRANSLATABLE_KEYS.forEach((key) => { selectedLanguages[key] = ""; });

    // ساخت translations object با key های خالی
    const emptyTranslations = {};
    TRANSLATABLE_KEYS.forEach((key) => { emptyTranslations[key] = {}; });

    return {
      id: this.$route.params.id,
      loaded: false,
      successMessage: false,
      company: null,
      platforms: [],
      photos: [],
      errors: [],

      // هر سکشن select جداگانه خودش رو داره
      selectedLanguages,

      updateForm: {
        translations: emptyTranslations,  // ← object نه array
        about_us: "",
        privacy_policy: "",
        short_description: "",
        terms_and_conditions: "",
        refund_policy: "",
        shipping_policy: "",
        company_highlights: "",
        logo: "",
        images: [],
        telephones: [],
        social_medias: [],
        achievements: [],
        fax: "",
        whatsapp_number: "",
        email: "",
        address: {
          city_id: "",
          latitude: "",
          longitude: "",
          address_description: "",
        },
      },
    };
  },

  mounted() {
    this.url = config("BACKEND_ROOT") + "/admin/company-info";
    this.fetchPlatforms();
    this.loadData();
  },

  computed: {
    // زبان‌هایی که هنوز برای این key اضافه نشدن
    availableLanguages() {
      return (key) => {
        const usedCodes = Object.keys(this.updateForm.translations[key] || {});
        return ALL_LANGUAGES.filter((lang) => !usedCodes.includes(lang.code));
      };
    },
  },

  methods: {
    // ── Data loading ──────────────────────────────
    async fetchPlatforms() {
      try {
        const res = await axios.get(config("BACKEND_ROOT") + "/admin/social-platforms");
        this.platforms = res.data.data;
      } catch (e) {
        console.error("Error fetching platforms:", e);
      }
    },

    loadData() {
      axios
          .get(this.url)
          .then((res) => {
            this.company = res.data.data;
            this.id = this.company.id;

            // کپی عمیق
            const form = JSON.parse(JSON.stringify(this.company));

            // address
            this.updateForm.address = form.address
                ? {
                  city_id: form.address.city?.id || "",
                  latitude: form.address.latitude || "",
                  longitude: form.address.longitude || "",
                  address_description: form.address.address_description || "",
                }
                : { city_id: "", latitude: "", longitude: "", address_description: "" };

            // فیلدهای ساده
            const simpleFields = [
              "about_us", "privacy_policy", "short_description",
              "terms_and_conditions", "refund_policy", "shipping_policy",
              "company_highlights", "logo", "fax", "whatsapp_number", "email",
            ];
            simpleFields.forEach((f) => { this.updateForm[f] = form[f] || ""; });

            this.updateForm.telephones   = form.telephones || [];
            this.updateForm.social_medias = form.social_medias || [];
            this.updateForm.images       = form.images || [];

            // achievements
            this.updateForm.achievements = (form.achievements || []).map((a) => ({
              title: a.title,
              photo: a.photo
                  ? "files" + a.photo.substring(a.photo.lastIndexOf("/"))
                  : "",
            }));

            // ── ترجمه‌ها ──
            // بک‌اند آرایه‌ای از { key, language_code, value } میفرسته
            // تبدیل به: { about_us: { ar: "...", fr: "..." }, ... }
            const translationsArray = form.translations || [];
            const translationsObj = {};
            TRANSLATABLE_KEYS.forEach((k) => { translationsObj[k] = {}; });

            translationsArray.forEach((t) => {
              if (translationsObj.hasOwnProperty(t.key)) {
                translationsObj[t.key][t.language_code] = t.value;
              }
            });
            this.updateForm.translations = translationsObj;

            // photos برای MultipleFilesUpload
            this.photos = (this.company.images || []).map((url) => ({ url }));

            this.loaded = true;
          })
          .catch((err) => {
            console.error(err);
            if (err.response?.status === 404) this.loaded = true;
          });
    },

    // ── Translations ──────────────────────────────
    addTranslation(key) {
      const lang = this.selectedLanguages[key];
      if (!lang) return;

      // اگه key وجود نداره بساز (reactive)
      if (!this.updateForm.translations[key]) {
        this.$set(this.updateForm.translations, key, {});
      }
      // اضافه کردن زبان جدید با مقدار خالی
      this.$set(this.updateForm.translations[key], lang, "");

      // reset select
      this.selectedLanguages[key] = "";
    },

    removeTranslation(key, langCode) {
      this.$delete(this.updateForm.translations[key], langCode);
    },

    // ── Telephones ────────────────────────────────
    addPhoneField(index) {
      const phones = [...this.updateForm.telephones];
      phones.splice(index, 0, "");
      this.updateForm.telephones = phones;
    },

    removePhoneField(index) {
      this.updateForm.telephones.splice(index, 1);
    },

    // ── Social medias ─────────────────────────────
    addSocialMedia() {
      this.updateForm.social_medias.push({ platform: "", url: "" });
    },

    removeSocialMedia(index) {
      this.updateForm.social_medias.splice(index, 1);
    },

    // ── Achievements ──────────────────────────────
    addAchievement() {
      this.updateForm.achievements.push({ title: "", photo: "" });
    },

    removeAchievement(index) {
      this.updateForm.achievements.splice(index, 1);
    },

    // ── Submit ────────────────────────────────────
    updateCompany() {
      this.errors = [];
      this.successMessage = false;

      // ساخت payload
      const payload = {
        about_us:            this.updateForm.about_us,
        privacy_policy:      this.updateForm.privacy_policy,
        short_description:   this.updateForm.short_description,
        terms_and_conditions: this.updateForm.terms_and_conditions,
        refund_policy:       this.updateForm.refund_policy,
        shipping_policy:     this.updateForm.shipping_policy,
        company_highlights:  this.updateForm.company_highlights,
        fax:                 this.updateForm.fax,
        whatsapp_number:     this.updateForm.whatsapp_number,
        email:               this.updateForm.email,
        telephones:          this.updateForm.telephones.filter(Boolean),
        address:             this.updateForm.address,
        social_medias:       this.updateForm.social_medias.filter(
            (s) => s.platform && s.url
        ),
        achievements:        this.updateForm.achievements.filter(
            (a) => a.title
        ),
        // ── ترجمه‌ها: تبدیل به آرایه برای بک‌اند ──
        // ساختار: [{ key, language, value }, ...]
        translatable: this.buildTranslationsPayload(),
      };

      // logo و images فقط اگه تغییر کردن
      if (this.updateForm.logo !== this.company?.logo) {
        payload.logo = this.updateForm.logo;
      }
      if (
          JSON.stringify(this.updateForm.images) !==
          JSON.stringify(this.company?.images)
      ) {
        payload.images = this.updateForm.images;
      }

      const request = this.id
          ? axios.put(`${this.url}/${this.id}`, payload)
          : axios.post(this.url, payload);

      request
          .then(() => {
            this.successMessage = true;
            window.scrollTo({ top: 0, behavior: "smooth" });
          })
          .catch((err) => {
            this.errors = Object.values(err.response?.data?.errors || {}).flat();
          });
    },

    // ── Helper: ساخت آرایه ترجمه برای ارسال ──────
    // خروجی: [{ key: "about_us", language: "ar", value: "..." }, ...]
    buildTranslationsPayload() {
      const result = [];
      TRANSLATABLE_KEYS.forEach((key) => {
        const langs = this.updateForm.translations[key] || {};
        Object.entries(langs).forEach(([language, value]) => {
          if (value !== null && value !== undefined && value !== "") {
            result.push({ key, language, value });
          }
        });
      });
      return result;
    },
  },
};
</script>
